// 2026-09-23 - FormuLearning - 최적화 결과 구획 — 성분 중요도 · 현재까지 요약 · 추천 카드 (이슈 #67 S4-7)
// 파일 위치: frontend/src/features/step4/OptimizationResult.tsx
// 목적: UI/UX v2.7 §4.6·§4.7 을 한 구획으로 묶는다. 순서 = Feature Importance → AI Summary «현재까지 요약» →
//       Recommended Formulation(M84-66·67 · M915-22). 분석(`GET …/analysis`)은 여기서 읽는다 — 중요도와 카드 예측
//       그래프가 같은 분석을 봐야 하기 때문이다(D-S4-2·3). 부모 재조회(`revision`)마다 다시 읽고, 새 분석이
//       대기·진행 중(`recalculating`)이면 5초마다 다시 읽어 끝나는 대로 바꾼다(BR-OPT-16).
//       «현재까지 요약»·카드는 **보고 있는 라운드** 기준이다(COMP-FEATURE-IMPORTANCE · COMP-RECOMMENDATION-CARD).
// 2026-09-23 수정 - 이슈 #67 S4-8: 중요도 아래 MOBO 진척도(`HvProgressChart`, 잠정 — Q-260917-03)를 붙였다.
//                   같은 분석을 읽는다. 용액별 f2 추이(통합표 아래 `BatchF2Chart`)는 그대로 둔다.

import { useEffect, useState } from 'react'
import { getStep4Analysis, type Step4AnalysisView, type Step4Run } from '../../api/step4'
import type { PhProfile, XVariable, YVariable } from '../../api/step3'
import type { RoundListItem } from '../../api/round'
import MarkdownText from '../../components/feedback/MarkdownText'
import FeatureImportance, { RecalcBadge } from './FeatureImportance'
import HvProgressChart from './HvProgressChart'
import RecommendationCard from './RecommendationCard'

const POLL_MS = 5000

/** `Opt. Round #n 추천 시점 · 09/23 16:05` — 요약은 그 추천이 만든 것이라 이후 바뀌지 않는다(BR-OPT-15). */
function recommendedAt(round: RoundListItem): string {
  const d = new Date(round.created_at)
  const p = (n: number) => String(n).padStart(2, '0')
  const at = Number.isNaN(d.getTime()) ? '' : ` · ${p(d.getMonth() + 1)}/${p(d.getDate())} ${p(d.getHours())}:${p(d.getMinutes())}`
  return `Opt. Round #${round.round_no - 1} 추천 시점${at}`
}

/** 그 라운드 추천이 해설 단계를 거쳤는가 — S4-6 이후 반영은 성공·실패 모두 `meta.ai_summary` 를 남긴다. */
const hadAiStep = (round: RoundListItem) => round.meta != null && 'ai_summary' in round.meta

function RoundSummary({ round }: { round: RoundListItem }) {
  if (round.round_type === 'initial') {
    return (
      <p className="text-[11px] text-text-secondary" data-testid="summary-initial">
        Initial Round 에는 추천 요약이 없습니다 — Optimization Round 를 선택하면 그 라운드 추천 시점의 요약을 봅니다.
      </p>
    )
  }
  const text = round.ai_summary_ko?.trim()
  return (
    <>
      <p className="mb-1 text-[10px] text-text-muted" data-testid="summary-basis">{recommendedAt(round)}</p>
      {text
        ? <>
            <MarkdownText text={text} />
            <p className="mt-1 text-[9px] italic text-text-secondary">⚠ AI 생성 내용 — 검토 후 사용</p>
          </>
        : <p className="text-[11px] text-text-secondary" data-testid="summary-empty">
            {hadAiStep(round) ? '해설을 만들지 못했습니다' : '이 라운드는 AI 해설 도입 전 추천이라 요약이 없습니다'}
          </p>}
    </>
  )
}

interface Props {
  projectId: string
  /** 부모가 Step 4 를 다시 읽을 때마다 올린다 — 분석도 다시 읽는다. */
  revision: number
  rounds: RoundListItem[]
  /** 보고 있는 라운드(M84-61) — 부모가 해석한 값. */
  currentRoundId: string | null
  /** 보고 있는 라운드의 opti 배치(`Step4Data.current_runs`) — 예측값·해설을 싣는다. */
  batches: Step4Run[]
  xVariables: XVariable[]
  totalWeightMg: number | null
  yVariables: YVariable[]
  /** Lot id → 대조약 프로파일(Step 3 `rld_experiment`). 라운드의 기준 대조약을 찾는다(작업계획 D14). */
  referenceByLot: Map<string, PhProfile[]>
  /** 라운드에 기준 대조약이 없을 때(구 데이터) 쓰는 활성 Lot 프로파일. */
  fallbackReference: PhProfile[]
}

export default function OptimizationResult({
  projectId, revision, rounds, currentRoundId, batches, xVariables, totalWeightMg, yVariables,
  referenceByLot, fallbackReference,
}: Props) {
  const [view, setView] = useState<Step4AnalysisView | null>(null)
  const [tick, setTick] = useState(0)

  useEffect(() => {
    let alive = true
    getStep4Analysis(projectId)
      .then((v) => { if (alive) setView(v) })
      .catch(() => { /* 분석은 부가 정보다 — 다음 계기에 다시 읽는다 */ })
    return () => { alive = false }
  }, [projectId, revision, tick])

  // 재계산 중이면 끝날 때까지 다시 읽는다 — 값은 그대로 두고 배지만 단다(D-S4-2)
  useEffect(() => {
    if (!view?.recalculating) return
    const t = setTimeout(() => setTick((n) => n + 1), POLL_MS)
    return () => clearTimeout(t)
  }, [view])

  // optimization round 가 하나도 없으면 결과 구획이 없다 — 분석도 null 이다(BR-OPT-16)
  if (!rounds.some((r) => r.round_type === 'optimization')) return null
  const round = rounds.find((r) => r.id === currentRoundId) ?? null
  const showCards = round?.round_type === 'optimization' && batches.length > 0
  const reference = (round?.reference_lot_id && referenceByLot.get(round.reference_lot_id)) || fallbackReference

  return (
    <div className="mb-4" data-testid="optimization-result">
      {/* §4.6 Feature Importance — 최적화 결과의 가장 첫 부분(M84-66) */}
      {view?.analysis
        ? <FeatureImportance view={view} />
        : view?.recalculating && (
            // 첫 분석이 아직 없다(첫 추천 직후) — 정본은 null 이면 구획을 그리지 않지만, 계산 중임은 알린다(D13)
            <p className="mb-3 flex items-center gap-2 text-[11px] text-text-secondary" data-testid="importance-pending">
              <span className="text-xs font-semibold uppercase tracking-wider">Feature Importance</span>
              <RecalcBadge /> 성분 중요도를 계산하고 있습니다.
            </p>
          )}

      {/* MOBO 진척도 — 전체 누적 관측(BR-OPT-17)이라 중요도와 함께 라운드 선택과 무관하다 */}
      {view?.analysis && <HvProgressChart view={view} rounds={rounds} />}

      {/* AI Summary ① 현재까지 요약 — 중요도 아래, 보고 있는 라운드 기준(M84-31 · M915-22) */}
      {round && (
        <section className="mb-3 rounded-md border p-3" data-testid="round-summary"
                 style={{ borderColor: 'var(--border-teal)', background: 'var(--bg-chip-teal)' }}>
          <p className="mb-1 text-[10px] font-semibold uppercase text-accent-teal">AI Summary — 현재까지 요약</p>
          <RoundSummary round={round} />
        </section>
      )}

      {/* §4.7 Recommended Formulation — 보고 있는 optimization round 의 배치 수만큼 가로(initial 에는 없다) */}
      {showCards && (
        <section data-testid="recommendation-cards">
          <h4 className="mb-2 text-xs font-semibold uppercase tracking-wider text-text-secondary">
            Recommended Formulation
          </h4>
          <div className="grid gap-2 overflow-x-auto pb-1"
               style={{ gridTemplateColumns: `repeat(${batches.length}, minmax(240px, 1fr))` }}>
            {batches.map((b) => (
              <RecommendationCard
                key={b.id}
                batch={b}
                xVariables={xVariables}
                totalWeightMg={totalWeightMg}
                yVariables={yVariables}
                profile={view?.analysis?.profiles?.[b.id]}
                profileMissing={!view?.analysis || view.recalculating ? 'pending' : 'none'}
                referenceProfiles={reference}
                hadAiStep={round != null && hadAiStep(round)}
              />
            ))}
          </div>
        </section>
      )}
    </div>
  )
}
